import { useMemo, useState } from 'react';
import AtlasEmptyState from './AtlasEmptyState';
import { ONBOARDING_COPY } from './onboardingCopy';
import './PulseRail.css';
import { PULSE_GROUPS } from '../../features/pulseRail';
import { t } from '../../i18n';

const GROUP_COLOR = {
  movement: 'var(--jade)',
  place: 'var(--coral)',
  moment: 'var(--sun)',
  data: 'var(--sky)',
};

const GROUP_LABEL = {
  movement: 'Deslocamento',
  place: 'Lugares',
  moment: 'Marcos',
  data: t('web.pulse.mediaDiary'),
};

function SummaryColumn({ summary, total }) {
  if (!summary) return null;
  return (
    <div className="av-pulserail-summary">
      <span>{summary.rangeLabel}</span>
      <strong>{summary.daysLabel}</strong>
      <small>{total} registros</small>
    </div>
  );
}

// Pulse Rail (fase 3.1/3.2/8 — docs/plans/overhaul-ux-atlas-vivo.md). Renderiza
// o modelo (buildPulseRailModel) como barras de densidade empilhadas por grupo,
// com a coluna de resumo (período + total de dias) igual ao render de
// referência. Hover/click emitem a posição temporal (0..1) e o bucket pro sync
// com o mapa.
function PulseRail({ model, summary, onHoverPosition, onSelectPosition, onImportData }) {
  const [activeBucket, setActiveBucket] = useState(null);
  const [hoverLiveMessage, setHoverLiveMessage] = useState('');

  const bars = useMemo(() => {
    if (!model || !model.valid || model.maxBucketCount === 0) return [];
    const bins = model.buckets.length;
    const widthPct = 100 / bins;
    return model.buckets.map((bucket) => {
      let offset = 0;
      const segments = PULSE_GROUPS.filter((group) => bucket.groups[group] > 0).map((group) => {
        const heightPct = (bucket.groups[group] / model.maxBucketCount) * 100;
        const seg = { group, heightPct, offset };
        offset += heightPct;
        return seg;
      });
      return { index: bucket.index, xPct: bucket.index * widthPct, widthPct, count: bucket.count, segments };
    });
  }, [model]);

  if (!model || !model.valid) {
    return (
      <div className="av-pulserail-inner">
        <SummaryColumn summary={summary} total={0} />
        <div className="av-pulserail-main av-pulserail-empty">
          <AtlasEmptyState {...ONBOARDING_COPY.pulseInvalidPeriod} compact />
        </div>
      </div>
    );
  }

  if (model.maxBucketCount === 0) {
    return (
      <div className="av-pulserail-inner">
        <SummaryColumn summary={summary} total={0} />
        <div className="av-pulserail-main av-pulserail-empty">
          <AtlasEmptyState
            {...ONBOARDING_COPY.pulseEmptyPeriod}
            actionLabel={onImportData ? ONBOARDING_COPY.pulseEmptyPeriod.cta : undefined}
            onAction={onImportData}
            compact
          />
        </div>
      </div>
    );
  }

  const emit = (bucketIndex, handler) => {
    if (!handler) return;
    const bins = model.buckets.length;
    handler((bucketIndex + 0.5) / bins, model.buckets[bucketIndex]);
  };

  return (
    <div className="av-pulserail-inner">
      <div className="av-sr-only" aria-live="polite" role="status" aria-label="Status do Pulse Rail">
        {hoverLiveMessage}
      </div>

      <SummaryColumn summary={summary} total={model.total} />

      <div className="av-pulserail-main">
        <div
          className="av-pulserail-track"
          role="group"
          aria-label={`Pulse Rail com ${model.total} eventos`}
        >
          {bars.map((bar) => (
            <button
              key={bar.index}
              type="button"
              className={`av-pulserail-bucket${bar.index === activeBucket ? ' is-active' : ''}`}
              style={{ left: `${bar.xPct}%`, width: `${bar.widthPct}%` }}
              aria-label={`Bloco ${bar.index + 1}: ${bar.count} eventos`}
              onMouseEnter={() => {
                setActiveBucket(bar.index);
                setHoverLiveMessage(`Bloco ${bar.index + 1}: ${bar.count} eventos`);
                emit(bar.index, onHoverPosition);
              }}
              onMouseLeave={() => {
                setActiveBucket(null);
                setHoverLiveMessage('');
                if (onHoverPosition) onHoverPosition(null, null);
              }}
              onFocus={() => setActiveBucket(bar.index)}
              onClick={() => emit(bar.index, onSelectPosition)}
            >
              {bar.segments.map((seg) => (
                <span
                  key={seg.group}
                  className="av-pulserail-seg"
                  style={{
                    height: `${seg.heightPct}%`,
                    bottom: `${seg.offset}%`,
                    background: GROUP_COLOR[seg.group],
                  }}
                />
              ))}
            </button>
          ))}
        </div>

        <div className="av-pulserail-legend" aria-hidden="true">
          {PULSE_GROUPS.filter((group) => model.groupTotals[group] > 0).map((group) => (
            <span key={group} className="av-pulserail-legend-item">
              <span className="av-pulserail-dot" style={{ background: GROUP_COLOR[group] }} />
              {GROUP_LABEL[group]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PulseRail;
